import { baseApi } from './baseApi';

/**
 * Llamar a Gustavo haciéndose pasar por un agente.
 *
 * Soporte elige inmobiliaria y agente, y habla con Gustavo como si fuera él: las tools
 * corren con sus permisos y su oficina, y lo que se agenda queda agendado de verdad.
 */
export interface VozTenant {
  id: string; nombre: string; slug: string;
  /** Cuántos agentes llamables tiene. Las que quedan en cero no se listan. */
  agentes: number;
}
export interface VozAgente {
  id: string; nombre: string;
  /** `null` en 49 de los 139: la ficha se dibuja con iniciales. */
  avatar: string | null;
  rol: string; oficina: string | null;
}
export interface VozSesion {
  /** Token de impersonación, corto. Con esto corre la llamada entera. */
  token: string;
  /** La firma para abrir el WebSocket con ElevenLabs. La emite Phylax, que es donde vive
   *  la key — igual que el TTS. `null` si no se pudo pedir: el panel dice por qué en vez
   *  de dejar un botón que no hace nada. */
  signedUrl: string | null;
  /**
   * El prompt REAL de Gustavo, armado para este agente.
   *
   * Viaja como override al abrir la llamada en vez de estar escrito a mano en el panel de
   * ElevenLabs. Es lo que hace que la llamada sepa quién llama por el mismo motivo que lo
   * sabe WhatsApp — y lo que evita que haya dos Gustavos con dos prompts que se separan.
   */
  prompt: string;
  /**
   * Las tools que el agente de voz va a poder pedir, según el backend.
   *
   * El navegador las compara con las que registró antes de abrir la llamada: si falta
   * alguna, el SDK no la ignora — corta la sesión entera con "is not defined on client".
   */
  tools: string[];
  /** Lo primero que dice al atender, ya con el nombre adentro. */
  saludo: string;
  agente: VozAgente;
  /**
   * Las definiciones completas de las tools, para el override de ElevenLabs.
   *
   * ⛔ Solo sirven si el Agent tiene habilitado el override de **Tools** en Security. Sin
   * eso ElevenLabs las ignora en silencio y la llamada usa las que estén en el panel.
   */
  toolDefs?: Array<{ type: 'client'; name: string; description: string; parameters: unknown }>;
}

export interface SavedFlowTool {
  order: number; toolName: string; module: string | null;
  status: 'ok' | 'error'; durationMs: number | null;
  args: Record<string, unknown>; output: string | null;
  errorMessage: string | null; missing: boolean;
}
/** Una persona con sesión viva en un canal (satélites que orbitan el orbe del cerebro). */
export interface Presente {
  id:          string;
  canal:       'crm' | 'whatsapp' | 'web' | 'phylax';
  nombre:      string;
  avatar:      string | null;
  detalle:     string | null;
  telefono:    string | null;
  mensajes:    number;
  ultimoAt:    string;
  ultimoTexto: string | null;
  link:        string | null;
}

export interface PresenciaPorCanal {
  ventanaMin: number;
  canales: Record<'crm' | 'whatsapp' | 'web' | 'phylax', Presente[]>;
  /** Canales que no se pudieron consultar (ej. Phylax caído): la UI lo avisa. */
  degradados: Array<'crm' | 'whatsapp' | 'web' | 'phylax'>;
}

/** La ficha de un agente para el monitor (solo soporte). */
export interface FichaDeAgente { id: string; nombre: string | null; avatar: string | null; oficina: string | null }

export interface SavedFlow {
  turnId: string; conversationId: string | null; userId: string; userName: string | null;
  /** Foto del agente (User.avatar), resuelta al servir. */
  userAvatar?: string | null;
  phone?: string | null;
  channel: 'crm' | 'whatsapp';
  /** De qué plataforma y país es el turno. Lo resuelve el backend desde `flow.tenantId`. */
  plataforma?: 'imperium' | 'booster' | null;
  pais?: string | null;
  requestText: string; requestAttachments?: string[] | null; proactive?: boolean; responseText: string | null;
  /** Lo que Gustavo mandó por WhatsApp en la respuesta (piezas, fotos, la del Studio). */
  responseAttachments?: string[] | null;
  /** Créditos que consumió el turno, y si se descontaron. Null en turnos viejos. */
  credits?: number | null; creditsCharged?: boolean | null;
  status: 'completed' | 'error'; toolCount: number; hasError: boolean;
  deliveryStatus?: 'sent' | 'delivered' | 'read' | 'failed' | null;
  deliveredAt?: string | null; readAt?: string | null;
  /** Salió fuera de la ventana de servicio de 24 h de WhatsApp (medición, no bloqueo). */
  outside24h?: boolean;
  /** El pedido llegó dictado: `requestText` es la transcripción, no lo que tecleó. */
  voiceNote?: boolean;
  /** Los audios de la charla, para escucharla en vez de leer la transcripción. */
  audioInUrl?: string | null;
  audioOutUrl?: string | null;
  /** El agente contestó CITANDO un mensaje del hilo: sin esto, un "dale, mandalo" del
   *  historial no dice sobre qué. Ya viene enmascarado del backend. */
  quoted?: { text: string; from: 'gustavo' | 'agente' } | null;
  horasDesdeUltimoInbound?: number | null;
  /** Palancas de IA del turno ("packs+cache"…). Con "packs" el monitor lo marca v2. */
  variant?: string | null;
  /** Métricas del turno de Jarvis (V7/V8): cola, demora del modelo, tools. Solo en turnos Jarvis. */
  native?: {
    queueMs?: number; relayMs?: number; wallMs?: number; firstTextMs?: number;
    toolMs?: number; toolCalls?: number; totalMs?: number;
    usage?: { input_tokens?: number; output_tokens?: number; input_tokens_details?: { cached_tokens?: number } };
  } | null;
  toolSelection?: {
    version: 6;
    workflow?: { cost?: { knownUsd: number; requests: number; unknown: number }; taskId: string; name: string; status: string; steps: Array<{ tool: string; status: string }> };
    costs?: { knownCostUsd: number; complete: boolean; requests: Array<{ provider: string; model: string; purpose: string; promptTokens: number; cachedTokens: number; completionTokens: number; costUsd: number | null }> };
    /** Las guardias JEV del turno: qué decidió la regla, qué dijo JEV y si frenó. */
    guardias?: Array<{ guardia: string; status: 'ok' | 'fallback'; propuesto?: string; elegido?: string; confidence?: number; veto: boolean; aplicado: boolean }>;
    catalogCount: number;
    steps: Array<{ reason: 'initial' | 'discover'; available: string[]; omitted: string[] }>;
    jev?: Array<{
      stage?: 'initial' | 'discover'; status: 'ok' | 'fallback'; mode: 'shadow' | 'assist' | 'active';
      domain?: string; operation?: string; scope?: string; contextual?: number;
      selectedTool?: string; confidence?: number; gatewayConfidence?: number;
      candidateCount: number; shortlistCount?: number; latencyMs: number; inputTokens?: number;
      selectionApplied?: boolean; selectionReason?: string; reason?: string;
    }>;
    conversationState?: {
      requested: boolean; reason: 'jev_context' | 'explicit_reference' | 'self_contained';
      reused?: boolean; fallback?: string; compactThreshold?: number;
    };
    direct?: { toolName: string; reason: string; outcome?: 'ok' | 'error'; zeroModelRequests: true };
  } | null;
  tools: SavedFlowTool[]; startedAt: string; endedAt: string; createdAt: string;
}
export interface SavedFlowsResponse { count: number; flows: SavedFlow[]; }
export interface GustavoV7Pilot {
  enabled: boolean; version: 7 | 8; stableVersion: 6;
  /** `toda_la_red`: Jarvis para todos (flag en `all`), sin lista de usuarios; `piloto`: los de la lista. */
  alcance?: 'piloto' | 'toda_la_red';
  users: Array<{ id: string; name: string; isQa: boolean }>;
  capacity?: { configured: number; healthy: number; busy: number; free: number; state: 'offline' | 'busy' | 'degraded' | 'ready' };
}
/** Charla agregada (agente+canal) para la lista estilo WhatsApp del live. */
export interface SavedChat {
  userId: string; channel: 'crm' | 'whatsapp'; userName: string | null;
  userAvatar?: string | null;
  phone?: string | null;
  lastText: string; lastTs: string; count: number; hasError: boolean;
  /** Cuántos salientes de esta charla salieron fuera de la ventana de 24 h de WA. */
  outside24hCount?: number;
  /** Soporte tomó el control: Gustavo no le contesta a este agente. */
  manual?: { activo: boolean; desde?: string | null; porNombre?: string | null } | null;
}
export interface SavedChatsResponse { count: number; chats: SavedChat[]; }
export interface GustavoFlowsParams {
  limit?: number; channel?: 'crm' | 'whatsapp'; userId?: string;
  onlyErrors?: boolean; from?: string; to?: string; before?: string;
}

export type ChatMessageRole = 'user' | 'assistant';

export interface ChatMessage {
  role:      ChatMessageRole;
  content:   string;
  /** Por dónde entró/salió (hilo único CRM+WhatsApp). */
  channel?:  'crm' | 'whatsapp';
  createdAt: string;
}

export interface ConversationSummary {
  _id:           string;
  title:         string;
  messageCount:  number;
  lastMessageAt: string;
  createdAt:     string;
  lastMessage?:  { role: 'user' | 'assistant'; content: string };
}

export interface ConversationDetail {
  _id:           string;
  title:         string;
  /** UNA PÁGINA del hilo (la última por defecto), no la conversación entera. */
  messages:      ChatMessage[];
  /** Mensajes visibles que tiene el hilo completo. */
  total:         number;
  /** Queda historial más arriba de esta página. Ver `useHistorialGustavo`. */
  hasMore:       boolean;
  createdAt:     string;
  lastMessageAt: string;
}

export interface GustavoAuditUser {
  id:     string;
  name:   string;
  email?: string;
  role?:  string;
  avatar?: string;
}

export interface GustavoAuditRow {
  _id:           string;
  createdAt:     string;
  user:          GustavoAuditUser | null;
  conversationId: string | null;
  toolName:       string;
  status:         'ok' | 'error';
  writeAction:    boolean;
  resultModel:    string | null;
  resultId:       string | null;
  durationMs:     number;
  errorMessage:   string | null;
  hasSnapshot:    boolean;
  argsPreview:    string[];
}

export interface GustavoAuditDetail extends Omit<GustavoAuditRow, 'hasSnapshot' | 'argsPreview'> {
  args:           Record<string, unknown>;
  resultSnapshot: Record<string, unknown> | null;
}

export interface GustavoAuditList {
  data: GustavoAuditRow[];
  pagination: { total: number; page: number; limit: number; totalPages: number };
}

export interface GustavoAuditFilters {
  from?:        string;
  to?:          string;
  userId?:      string;
  toolName?:    string;
  status?:      'ok' | 'error';
  writeAction?: boolean;
  page?:        number;
  limit?:       number;
}

export interface GustavoStats {
  range: { from: string; to: string };
  totals: {
    total:         number;
    errors:        number;
    writes:        number;
    reads:         number;
    errorRate:     number;
    avgDurationMs: number;
  };
  byDay:  Array<{ date: string; total: number; errors: number }>;
  byTool: Array<{ toolName: string; count: number; errors: number; errorRate: number; avgDurationMs: number }>;
  byUser: Array<{ userId?: string; name: string; email: string | null; role: string | null; count: number; errors: number }>;
  writeBreakdown: Array<{ toolName: string; count: number; errors: number }>;
}

export interface AgentConsumptionRow {
  agentId:      string;
  name:         string;
  email:        string;
  avatar?:      string;
  totalCredits: number;
  transactions: number;
  estimatedUSD: number | null;
}

export interface AgentConsumptionResponse {
  creditValueUSD: number;
  agents: AgentConsumptionRow[];
  totals: {
    totalCredits: number;
    totalAgents:  number;
    estimatedUSD: number | null;
  };
}


// ─── Fallas de Gustavo (incidentes agrupados) ─────────────────────────────
/**
 * ⚠️ Es una COPIA del enum del backend (`models/GustavoIncident.ts`) y ya había divergido:
 * le faltaban `intent_miss` y `calidad_qc` desde el 14/08. No rompe la pantalla porque el
 * label viaja resuelto en `kindLabel` —el back lo manda con el incidente—, pero cualquier
 * `switch` exhaustivo que se escriba acá va a quedar incompleto sin que el compilador avise.
 */
export type IncidentKind = 'accion_fantasma' | 'escritura_fallida' | 'no_supo_responder'
  | 'correccion_agente' | 'tool_rota' | 'link_inventado' | 'intent_miss' | 'calidad_qc'
  | 'candado_no_alcanzo' | 'calidad_tarea';
export type IncidentSeverity = 'critical' | 'high' | 'medium';
export type IncidentStatus = 'new' | 'reviewing' | 'fixed' | 'false_positive';

export interface IncidentSample {
  conversationId?: string; turnId?: string; userId?: string; userName?: string;
  userText?: string; assistantText?: string;
  tools?: Array<{ name: string; status: string; error?: string }>;
  at: string;
}
export interface GustavoIncident {
  _id: string;
  kind: IncidentKind; kindLabel: string; severity: IncidentSeverity;
  title: string; hypothesis?: string; confidence?: number; evaluated: boolean;
  occurrences: number; agentsAffected: number;
  samples: IncidentSample[];
  status: IncidentStatus;
  bugReportId?: string;
  /** Instrumento antirrecurrencia: el caso golden creado desde este incidente. */
  goldenTestId?: string;
  /** Instrumento antirrecurrencia: commit o test que canda el fix. */
  lockRef?: string;
  firstSeenAt: string; lastSeenAt: string;
}
export interface IncidentsSummary {
  abiertos: number; criticos: number; ocurrencias: number; agentes: number; ultimas24h: number;
  porTipo: Array<{ kind: string; label: string; n: number }>;
  /** Vivos pero ya con ticket: salen de la bandeja y se trabajan en /admin/soporte. */
  enElInboxDeBugs: number;
}

export interface CambioGustavo {
  titulo: string;
  tipo: 'feature' | 'fix' | 'ajuste';
  fecha: string;
  origen?: string;
  detalle?: string;
  /** true = se registró solo al cerrar un incidente o un ticket. */
  auto?: boolean;
}
export interface VersionGustavo {
  version: string; nombre: string; desde: string; resumen: string;
  cambios: CambioGustavo[];
}
export interface GustavoChangelog {
  version: { actual: string; alcance: string; oficinasPiloto: string[]; modoFlag: string };
  /** Qué modelo responde en cada sistema (sale de la config del proceso deployado). */
  modelos?: {
    v4: { base: string; complejos: string; api: string };
    v3: { base: string; complejos: string; api: string };
  };
  /**
   * Los sistemas que están ATENDIENDO, ya filtrados por el backend. `modelos` (arriba) es
   * el catálogo completo —el changelog necesita nombrar a v3 para contar su historia—;
   * esto es lo que hay que dibujar como vivo. Si con `targeted` todas las oficinas de la
   * red están en la flag, v3 no viene: no atiende a nadie.
   */
  sistemas?: Array<{
    version: 'v3' | 'v4' | 'v5' | 'v6' | 'v7' | 'v8'; alcance: string;
    base: string; complejos: string; api: 'responses' | 'chat-completions';
  }>;
  prompt: { version: number; label: string; caracteres: number; desde: string } | null;
  promptsAnteriores: Array<{ version: number; label: string; caracteres: number }>;
  versiones: VersionGustavo[];
}

// ─── Quality (PLAN_QUALITY.md, Q3) ────────────────────────────────────────
export type EstadoHallazgo = 'nuevo' | 'real' | 'falso_positivo' | 'arreglado';
export interface QualityMuestra {
  turnId: string; conversationId: string; userId?: string; userName?: string; tenantId?: string;
  pedido: string; respuesta: string; detalle: string; tambien: string[];
  senal?: 'corrigio' | 'repitio' | 'reporto'; ruta?: string; at: string;
}
export interface QualityFinding {
  _id: string; firma: string; contrato: string; clase: string; severidad: 'alta' | 'media'; detalle: string;
  estado: EstadoHallazgo; ocurrencias: number; agentes: string[]; senales: number; muestras: QualityMuestra[];
  primeraVez: string; ultimaVez: string; etiquetadoEn?: string; nota?: string;
  /** El incidente que abrió solo (contrato con ≥80% y 30 revisados). */
  incidentId?: string;
  /** El arreglo (Q4): Codex → push → mensaje al agente. */
  caso?: QualityCaso;
}
export type EstadoCaso = 'en_cola' | 'en_codex' | 'resuelto' | 'fallo' | 'push_pedido' | 'subido' | 'mensaje_pedido' | 'mensaje_listo' | 'avisado';
/** Los que esperan al worker de soporte: mientras haya uno, la lista se refresca sola. */
export const ESTADOS_CASO_ACTIVOS: readonly EstadoCaso[] = ['en_cola', 'en_codex', 'push_pedido', 'mensaje_pedido'];
export interface QualityCaso {
  estado: EstadoCaso; turnId: string;
  rama?: string; commit?: string; resumen?: string; archivos?: string[]; verificacion?: string;
  error?: string | null; sinCredito?: boolean;
  mensaje?: { texto: string; respuestaGustavo?: string; canal?: 'whatsapp' | 'crm'; enviadoEn?: string };
  historial: Array<{ estado: EstadoCaso; at: string; nota?: string }>;
  actualizado: string;
}
/** Un hallazgo de la vista previa: todavía no está guardado. */
export interface QualityVista {
  hallazgo: { firma: string; contrato: string; clase: string; severidad: 'alta' | 'media'; detalle: string; tambien: string[]; senal?: 'corrigio' | 'repitio' | 'reporto' };
  muestra: QualityMuestra;
}
export interface QualityMetricaContrato {
  contrato: string; hallazgos: number; ocurrencias: number; reales: number; falsos: number; sinEtiquetar: number;
  precision: number | null; automatico: boolean;
}

export type ContinuousQaBucket = 'revalidacion' | 'critico' | 'cobertura' | 'contexto';
export type ContinuousQaStatus = 'preparada' | 'corriendo' | 'completa' | 'parcial' | 'fallo' | 'cancelada';
export interface ContinuousQaQueueItem {
  position: number; suite: 'dia-a-dia' | 'propiedades' | 'crm'; turn: number; label: string; bucket: ContinuousQaBucket;
}
export interface ContinuousQaResult extends ContinuousQaQueueItem {
  ok: boolean; tools: string[]; expectedTools: string[]; answer?: string; requestError?: string;
  toolErrors: string[]; openAiCostUsd: number; conservativeCostUsd: number; conversationId?: string; turnId?: string;
  /** Contratos de Quality rotos ("contrato: detalle"). */
  violaciones?: string[];
  /** Variante del turno: dice qué motor contestó (la batería exige V8). Ausente en corridas viejas. */
  variant?: string;
  latencyMs?: number;
}
export interface ContinuousQaConfig {
  enabled: boolean; timezone: 'America/Lima'; hour: number; dailyBudgetUsd: number;
  operationalBudgetUsd: number; maxMessages: number; approvalMode: 'manual';
}
export interface ContinuousQaRun {
  _id: string | null; dateKey: string; source: 'scheduled' | 'manual'; status: ContinuousQaStatus;
  scheduledFor: string; queue: ContinuousQaQueueItem[]; results: ContinuousQaResult[];
  budgetUsd: number; operationalBudgetUsd: number; openAiCostUsd: number; conservativeCostUsd: number;
  completed: number; passed: number; failed: number; stoppedReason?: string; startedAt?: string; finishedAt?: string;
}
export interface ContinuousQaDashboard {
  config: ContinuousQaConfig; nextRun: ContinuousQaRun; latestRun: ContinuousQaRun | null; runs: ContinuousQaRun[];
}

export const gustavoApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    listConversations: build.query<ConversationSummary[], void>({
      query: () => '/ai/conversations',
      transformResponse: (res: { data: ConversationSummary[] }) => res.data,
      providesTags: ['GustavoConversation'],
    }),

    getConversation: build.query<ConversationDetail, string>({
      query: (id) => `/ai/conversations/${id}`,
      transformResponse: (res: { data: ConversationDetail }) => res.data,
      providesTags: (_r, _e, id) => [{ type: 'GustavoConversation' as const, id }],
    }),

    deleteConversation: build.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/ai/conversations/${id}`, method: 'DELETE' }),
      transformResponse: (res: { data: { ok: boolean } }) => res.data,
      invalidatesTags: ['GustavoConversation'],
    }),

    listGustavoAudit: build.query<GustavoAuditList, GustavoAuditFilters | void>({
      query: (filters) => {
        const params = new URLSearchParams();
        const f = filters ?? {};
        Object.entries(f).forEach(([k, v]) => {
          if (v === undefined || v === null || v === '') return;
          params.set(k, String(v));
        });
        const qs = params.toString();
        return `/ai/audit${qs ? `?${qs}` : ''}`;
      },
      transformResponse: (res: { data: GustavoAuditList }) => res.data,
    }),

    getGustavoAuditById: build.query<GustavoAuditDetail, string>({
      query: (id) => `/ai/audit/${id}`,
      transformResponse: (res: { data: GustavoAuditDetail }) => res.data,
    }),

    undoGustavoAction: build.mutation<{ ok: boolean }, string>({
      query: (logId) => ({ url: `/ai/actions/${logId}/undo`, method: 'POST' }),
      transformResponse: (res: { data: { ok: boolean } }) => res.data,
    }),

    getGustavoStats: build.query<GustavoStats, { from?: string; to?: string } | void>({
      query: (f) => {
        const p = new URLSearchParams();
        if (f?.from) p.set('from', f.from);
        if (f?.to)   p.set('to', f.to);
        const qs = p.toString();
        return `/ai/stats${qs ? `?${qs}` : ''}`;
      },
      transformResponse: (res: { data: GustavoStats }) => res.data,
    }),

    getGustavoConsumption: build.query<AgentConsumptionResponse, { from?: string; to?: string } | void>({
      query: (f) => {
        const p = new URLSearchParams();
        if (f?.from) p.set('from', f.from);
        if (f?.to)   p.set('to',   f.to);
        const qs = p.toString();
        return `/ai/admin/consumption${qs ? `?${qs}` : ''}`;
      },
      transformResponse: (res: { data: AgentConsumptionResponse }) => res.data,
    }),

    getGustavoGreeting: build.query<{
      greeting: string;
      hasItems: boolean;
      greetingMode: 'onboarding' | 'impulso' | 'normal';
      suggestedPrompts?: string[];
      initialPrompt?: string;
    }, void>({
      query: () => '/ai/greeting',
      transformResponse: (res: { data: { greeting: string; hasItems: boolean; greetingMode: 'onboarding' | 'impulso' | 'normal'; suggestedPrompts?: string[]; initialPrompt?: string } }) => res.data,
    }),

    /** EL hilo del agente: uno solo, compartido entre el CRM y WhatsApp. */
    getMainConversation: build.query<{ conversationId: string; title: string; messageCount: number; lastMessageAt: string }, void>({
      query: () => '/ai/main-conversation',
      transformResponse: (res: { data: { conversationId: string; title: string; messageCount: number; lastMessageAt: string } }) => res.data,
      providesTags: ['GustavoConversation'],
    }),

    /** Avisos de Gustavo sin leer → puntito del botón flotante. */
    getGustavoNotices: build.query<{ count: number; lastAt: string | null; preview: string | null; conversationId: string | null }, void>({
      query: () => '/ai/notices',
      transformResponse: (res: { data: { count: number; lastAt: string | null; preview: string | null; conversationId: string | null } }) => res.data,
      providesTags: ['GustavoNotices'],
    }),

    /** El agente abrió el chat: apaga el puntito. */
    markGustavoNoticesSeen: build.mutation<{ count: number }, void>({
      query: () => ({ url: '/ai/notices/seen', method: 'POST' }),
      transformResponse: (res: { data: { count: number } }) => res.data,
      invalidatesTags: ['GustavoNotices'],
    }),


    // ─── Fallas de Gustavo (incidentes agrupados) ─────────────────────────
    getIncidentsSummary: build.query<IncidentsSummary, void>({
      query: () => '/ai/monitor/incidents/summary',
      transformResponse: (res: { data: IncidentsSummary }) => res.data,
      providesTags: ['GustavoIncident'],
    }),
    // `conTicket` trae justo los que la bandeja esconde: los que ya son un bug.
    listIncidents: build.query<GustavoIncident[], { status?: IncidentStatus; kind?: string; limit?: number; conTicket?: boolean } | void>({
      query: (params) => ({ url: '/ai/monitor/incidents', params: params ?? {} }),
      transformResponse: (res: { data: { incidents: GustavoIncident[] } }) => res.data.incidents,
      providesTags: ['GustavoIncident'],
    }),
    updateIncident: build.mutation<GustavoIncident, { id: string; status: IncidentStatus; lockRef?: string; override?: boolean }>({
      // `lockRef` / `override` son el circuito de cierre con instrumento: el backend
      // rechaza con 400 pasar a `fixed` sin goldenTestId ni lockRef, salvo override.
      query: ({ id, ...body }) => ({ url: `/ai/monitor/incidents/${id}`, method: 'PATCH', body }),
      transformResponse: (res: { data: GustavoIncident }) => res.data,
      invalidatesTags: ['GustavoIncident'],
    }),
    /** Arma un caso golden BORRADOR (inactivo) a partir del extracto del incidente. */
    generateIncidentGoldenDraft: build.mutation<{ ok: boolean; test: { _id: string; name: string } }, string>({
      query: (id) => ({ url: `/ai/monitor/incidents/${id}/golden-draft`, method: 'POST' }),
      // El endpoint devuelve `{ ok, test }` plano; si algún día lo envuelven en
      // `{ data }` como el resto del monitor, esto lo banca igual.
      transformResponse: (res: any) => res?.data ?? res,
      invalidatesTags: ['GustavoIncident', 'GustavoGolden'],
    }),
    promoteIncidentToBug: build.mutation<{ bugReportId: string; incidentId: string; alreadyPromoted?: boolean }, string>({
      query: (id) => ({ url: `/ai/monitor/incidents/${id}/promote`, method: 'POST' }),
      transformResponse: (res: { data: any }) => res.data,
      invalidatesTags: ['GustavoIncident', 'BugReport'],
    }),
    runIncidentDetection: build.mutation<{ candidatos: number; nuevos: number; actualizados: number; descartados: number; evaluados: number }, { horas?: number } | void>({
      query: (body) => ({ url: '/ai/monitor/incidents/run', method: 'POST', body: body ?? {} }),
      transformResponse: (res: { data: any }) => res.data,
      invalidatesTags: ['GustavoIncident'],
    }),

    // ─── Quality: hallazgos que verifican la tarea (Q3) ───────────────────
    listQualityFindings: build.query<QualityFinding[], { estado?: EstadoHallazgo | 'abiertos'; contrato?: string; horas?: number } | void>({
      query: (params) => ({ url: '/ai/monitor/quality/findings', params: params ?? {} }),
      transformResponse: (res: { data: { hallazgos: QualityFinding[] } }) => res.data.hallazgos,
      providesTags: ['QualityFinding'],
    }),
    getQualityMetricas: build.query<QualityMetricaContrato[], void>({
      query: () => '/ai/monitor/quality/metricas',
      transformResponse: (res: { data: { contratos: QualityMetricaContrato[] } }) => res.data.contratos,
      providesTags: ['QualityFinding'],
    }),
    labelQualityFinding: build.mutation<QualityFinding, { id: string; estado: EstadoHallazgo; nota?: string }>({
      query: ({ id, ...body }) => ({ url: `/ai/monitor/quality/findings/${id}`, method: 'PATCH', body }),
      transformResponse: (res: { data: { hallazgo: QualityFinding } }) => res.data.hallazgo,
      invalidatesTags: ['QualityFinding'],
    }),
    // ─── Quality · Q4: el arreglo (lo ejecuta quality-worker en la máquina de soporte) ───
    pasarQualityACodex: build.mutation<QualityFinding, string>({
      query: (id) => ({ url: `/ai/monitor/quality/findings/${id}/codex`, method: 'POST' }),
      transformResponse: (res: { data: { hallazgo: QualityFinding } }) => res.data.hallazgo,
      invalidatesTags: ['QualityFinding'],
    }),
    getQualityContexto: build.query<string, string>({
      query: (id) => `/ai/monitor/quality/findings/${id}/contexto`,
      transformResponse: (res: { data: { texto: string } }) => res.data.texto,
    }),
    pushQualityArreglo: build.mutation<QualityFinding, string>({
      query: (id) => ({ url: `/ai/monitor/quality/findings/${id}/push`, method: 'POST' }),
      transformResponse: (res: { data: { hallazgo: QualityFinding } }) => res.data.hallazgo,
      invalidatesTags: ['QualityFinding'],
    }),
    armarQualityMensaje: build.mutation<QualityFinding, string>({
      query: (id) => ({ url: `/ai/monitor/quality/findings/${id}/mensaje`, method: 'POST' }),
      transformResponse: (res: { data: { hallazgo: QualityFinding } }) => res.data.hallazgo,
      invalidatesTags: ['QualityFinding'],
    }),
    enviarQualityMensaje: build.mutation<QualityFinding, { id: string; texto: string }>({
      query: ({ id, texto }) => ({ url: `/ai/monitor/quality/findings/${id}/enviar`, method: 'POST', body: { texto } }),
      transformResponse: (res: { data: { hallazgo: QualityFinding } }) => res.data.hallazgo,
      invalidatesTags: ['QualityFinding'],
    }),
    runQuality: build.mutation<{ turnos: number; hallazgos: number; nuevos: number; actualizados: number; incidentes: number; vista: QualityVista[] }, { horas?: number; dias?: number; escribir?: boolean }>({
      query: (body) => ({ url: '/ai/monitor/quality/run', method: 'POST', body }),
      transformResponse: (res: { data: any }) => res.data,
      invalidatesTags: ['QualityFinding'],
    }),
    getContinuousQa: build.query<ContinuousQaDashboard, void>({
      query: () => '/ai/monitor/quality/continuous',
      transformResponse: (res: { data: ContinuousQaDashboard }) => res.data,
      providesTags: ['QualityFinding'],
    }),
    configureContinuousQa: build.mutation<ContinuousQaConfig, Partial<Pick<ContinuousQaConfig, 'enabled' | 'dailyBudgetUsd' | 'operationalBudgetUsd' | 'maxMessages'>>>({
      query: (body) => ({ url: '/ai/monitor/quality/continuous/config', method: 'PATCH', body }),
      transformResponse: (res: { data: { config: ContinuousQaConfig } }) => res.data.config,
      invalidatesTags: ['QualityFinding'],
    }),
    queueContinuousQaNow: build.mutation<ContinuousQaRun, void>({
      query: () => ({ url: '/ai/monitor/quality/continuous/runs', method: 'POST' }),
      transformResponse: (res: { data: { run: ContinuousQaRun } }) => res.data.run,
      invalidatesTags: ['QualityFinding'],
    }),

    // ─── Historial de flujos persistidos (consola en vivo · TTL 15 días) ──
    getGustavoV7Pilot: build.query<GustavoV7Pilot, void>({
      query: () => '/ai/monitor/v7-pilot',
      transformResponse: (res: { data: GustavoV7Pilot }) => res.data,
    }),
    listGustavoFlows: build.query<SavedFlowsResponse, GustavoFlowsParams | void>({
      query: (params) => ({ url: '/ai/monitor/flows', params: params ?? {} }),
      transformResponse: (res: { data: SavedFlowsResponse }) => res.data,
    }),
    /** Foto y oficina de los agentes de "Mensajes recientes". `ids` separados por coma. */
    getFichasAgentes: build.query<FichaDeAgente[], string>({
      query: (ids) => ({ url: '/ai/monitor/agentes', params: { ids } }),
      transformResponse: (res: { data: FichaDeAgente[] }) => res.data,
      keepUnusedDataFor: 600,
    }),
    /** Quién está hablando con Gustavo AHORA, por canal → satélites del cerebro.
     *  `demo: 1` devuelve gente inventada para poder mirar la vista con carga. */
    listGustavoPresencia: build.query<PresenciaPorCanal, { ventanaMin?: number; demo?: 1 } | void>({
      query: (params) => ({ url: '/ai/monitor/presencia', params: params ?? {} }),
      transformResponse: (res: { data: PresenciaPorCanal }) => res.data,
    }),
    // ─── Llamar a Gustavo como un agente ──────────────────────────────────
    /** Las inmobiliarias que tienen agentes llamables. */
    listVozTenants: build.query<VozTenant[], void>({
      query: () => '/ai/voice/tenants',
      transformResponse: (res: { data: VozTenant[] }) => res.data,
    }),
    /** Los agentes de una inmobiliaria. Se pide recién al elegirla. */
    listVozAgentes: build.query<VozAgente[], string>({
      query: (tenantId) => ({ url: '/ai/voice/agents', params: { tenantId } }),
      transformResponse: (res: { data: VozAgente[] }) => res.data,
    }),
    /** Abre la sesión: devuelve el token con el que corre la llamada. */
    /** Los nombres de las tools de voz. El navegador los registra ANTES de marcar. */
    listVozToolNames: build.query<string[], void>({
      query: () => '/ai/voice/tool-names',
      transformResponse: (r: { data: { tools: string[] } }) => r.data.tools,
    }),

    abrirSesionVoz: build.mutation<VozSesion, { tenantId: string; agentId: string }>({
      query: (body) => ({ url: '/ai/voice/session', method: 'POST', body }),
      transformResponse: (res: { data: VozSesion }) => res.data,
    }),

    /** Changelog de Gustavo: versión en producción, prompt activo y cambios por versión. */
    getGustavoChangelog: build.query<GustavoChangelog, void>({
      query: () => '/ai/monitor/changelog',
      transformResponse: (res: { data: GustavoChangelog }) => res.data,
    }),

    listGustavoChats: build.query<SavedChatsResponse, void>({
      query: () => '/ai/monitor/chats',
      transformResponse: (res: { data: SavedChatsResponse }) => res.data,
      providesTags: ['GustavoManual'],
    }),

    // ─── Control manual de una charla ─────────────────────────────────────
    /** Tomar o soltar el control: mientras está tomado, Gustavo no le contesta. */
    setGustavoManual: build.mutation<EstadoManual, { userId: string; activo: boolean }>({
      query: ({ userId, activo }) => ({
        url: `/ai/monitor/chats/${userId}/manual`, method: 'POST', body: { activo },
      }),
      transformResponse: (res: { data: EstadoManual }) => res.data,
      invalidatesTags: ['GustavoManual'],
    }),
    /** Manda un mensaje al agente como si lo escribiera Gustavo. */
    sendGustavoManual: build.mutation<{ waSent: boolean }, { userId: string; text: string }>({
      query: ({ userId, text }) => ({
        url: `/ai/monitor/chats/${userId}/manual/message`, method: 'POST', body: { text },
      }),
      transformResponse: (res: { data: { waSent: boolean } }) => res.data,
    }),

    // ─── Monitor de Gustavo (rol support) ─────────────────────────────────
    listMonitorConversations: build.query<MonitorConversationsList, MonitorListParams>({
      query: (params) => ({ url: '/ai/monitor/conversations', params }),
      transformResponse: (res: { data: MonitorConversationsList }) => res.data,
    }),
    getMonitorConversation: build.query<MonitorConversationDetail, string>({
      query: (id) => `/ai/monitor/conversations/${id}`,
      transformResponse: (res: { data: MonitorConversationDetail }) => res.data,
    }),
    getMonitorToolStats: build.query<MonitorToolStats, { from?: string; to?: string }>({
      query: (params) => ({ url: '/ai/monitor/tools/stats', params }),
      transformResponse: (res: { data: MonitorToolStats }) => res.data,
    }),
    getMonitorToolExecutions: build.query<MonitorToolExecutions, { name: string; limit?: number }>({
      query: ({ name, limit }) => ({ url: `/ai/monitor/tools/${name}/recent`, params: { limit } }),
      transformResponse: (res: { data: MonitorToolExecutions }) => res.data,
    }),
    getFeedbackStats: build.query<FeedbackStats, void>({
      query: () => '/ai/monitor/feedback/stats',
      transformResponse: (res: { data: FeedbackStats }) => res.data,
    }),
    listNegativeFeedbacks: build.query<NegativeFeedbacksList, { page?: number; limit?: number }>({
      query: (params) => ({ url: '/ai/monitor/feedback/negative', params }),
      transformResponse: (res: { data: NegativeFeedbacksList }) => res.data,
      providesTags: ['NegativeFeedback'],
    }),

    resolveFeedback: build.mutation<{ _id: string; resolution: string }, { id: string; resolution: 'applied' | 'dismissed' }>({
      query: ({ id, resolution }) => ({ url: `/ai/monitor/feedback/${id}/resolve`, method: 'PATCH', body: { resolution } }),
      transformResponse: (res: { data: { _id: string; resolution: string } }) => res.data,
      invalidatesTags: ['NegativeFeedback'],
    }),

    submitGustavoFeedback: build.mutation<{ ok: boolean }, { conversationId: string; messageIndex: number; rating: 1 | -1; comment?: string }>({
      query: (body) => ({ url: '/ai/feedback', method: 'POST', body }),
      transformResponse: (res: { data: { ok: boolean } }) => res.data,
    }),

    listAllGustavoTools: build.query<{ tools: GustavoToolEntry[] }, void>({
      query: () => '/ai/monitor/tools/all',
      transformResponse: (res: { data: { tools: GustavoToolEntry[] } }) => res.data,
      providesTags: ['ToolFlag'],
    }),
    setGustavoToolFlag: build.mutation<{ toolName: string; enabled: boolean }, { toolName: string; enabled: boolean; reason?: string }>({
      query: ({ toolName, ...body }) => ({ url: `/ai/monitor/tools/${toolName}/flag`, method: 'PATCH', body }),
      transformResponse: (res: { data: { toolName: string; enabled: boolean } }) => res.data,
      invalidatesTags: ['ToolFlag'],
    }),

    // ─── Reglas de Gustavo (Rule Generator IA) ─────────────────────────
    listGustavoRules: build.query<{ rules: GustavoRule[] }, void>({
      query: () => '/ai/monitor/rules',
      transformResponse: (res: { data: { rules: GustavoRule[] } }) => res.data,
      providesTags: ['GustavoRule'],
    }),
    generateGustavoRule: build.mutation<GeneratedRule, { text: string }>({
      query: (body) => ({ url: '/ai/monitor/rules/generate', method: 'POST', body }),
      transformResponse: (res: { data: GeneratedRule }) => res.data,
    }),
    generateGustavoRuleFromConversation: build.mutation<
      GeneratedRule & { context: { userPrompt: string; assistantResponse: string } },
      { conversationId: string; messageIndex: number; userComment?: string }
    >({
      query: (body) => ({ url: '/ai/monitor/rules/generate-from-conversation', method: 'POST', body }),
      transformResponse: (res: { data: GeneratedRule & { context: { userPrompt: string; assistantResponse: string } } }) => res.data,
    }),
    createGustavoRule: build.mutation<GustavoRule, CreateGustavoRuleBody>({
      query: (body) => ({ url: '/ai/monitor/rules', method: 'POST', body }),
      transformResponse: (res: { data: GustavoRule }) => res.data,
      invalidatesTags: ['GustavoRule'],
    }),
    updateGustavoRule: build.mutation<GustavoRule, { id: string; rule?: string; category?: string; audience?: RuleAudience; enabled?: boolean }>({
      query: ({ id, ...body }) => ({ url: `/ai/monitor/rules/${id}`, method: 'PATCH', body }),
      transformResponse: (res: { data: GustavoRule }) => res.data,
      invalidatesTags: ['GustavoRule'],
    }),
    deleteGustavoRule: build.mutation<void, string>({
      query: (id) => ({ url: `/ai/monitor/rules/${id}`, method: 'DELETE' }),
      invalidatesTags: ['GustavoRule'],
    }),

    // ─── Quality scan ─────────────────────────────────────────────────
    scanGustavoQuality: build.query<QualityScanResult, QualityScanParams>({
      query: (params) => ({ url: '/ai/monitor/quality/scan', params }),
      transformResponse: (res: { data: QualityScanResult }) => res.data,
    }),
    getGustavoQualityAnalysis: build.query<ConversationQuality, string>({
      query: (id) => `/ai/monitor/quality/conversations/${id}`,
      transformResponse: (res: { data: ConversationQuality }) => res.data,
    }),

    // ─── System prompt versioning ───────────────────────────────────
    getActiveGustavoPrompt: build.query<ActivePromptResponse, void>({
      query: () => '/ai/monitor/prompt/active',
      transformResponse: (res: { data: ActivePromptResponse }) => res.data,
      providesTags: ['GustavoPrompt'],
    }),
    listGustavoPromptVersions: build.query<{ versions: GustavoPromptVersion[] }, void>({
      query: () => '/ai/monitor/prompt/versions',
      transformResponse: (res: { data: { versions: GustavoPromptVersion[] } }) => res.data,
      providesTags: ['GustavoPrompt'],
    }),
    createGustavoPromptVersion: build.mutation<GustavoPromptVersion, { template: string; label?: string; notes?: string; activate?: boolean }>({
      query: (body) => ({ url: '/ai/monitor/prompt/versions', method: 'POST', body }),
      transformResponse: (res: { data: GustavoPromptVersion }) => res.data,
      invalidatesTags: ['GustavoPrompt'],
    }),
    activateGustavoPromptVersion: build.mutation<GustavoPromptVersion, string>({
      query: (id) => ({ url: `/ai/monitor/prompt/versions/${id}/activate`, method: 'POST' }),
      transformResponse: (res: { data: GustavoPromptVersion }) => res.data,
      invalidatesTags: ['GustavoPrompt'],
    }),
    deleteGustavoPromptVersion: build.mutation<void, string>({
      query: (id) => ({ url: `/ai/monitor/prompt/versions/${id}`, method: 'DELETE' }),
      invalidatesTags: ['GustavoPrompt'],
    }),

    // ─── Post-flight QC ────────────────────────────────────────────
    getGustavoQCConfig: build.query<QCConfig, void>({
      query: () => '/ai/monitor/qc/config',
      transformResponse: (res: { data: QCConfig }) => res.data,
      providesTags: ['GustavoQCConfig'],
    }),
    setGustavoQCConfig: build.mutation<QCConfig, { enabled?: boolean; samplePct?: number; alwaysCheckCategories?: string[] }>({
      query: (body) => ({ url: '/ai/monitor/qc/config', method: 'PATCH', body }),
      transformResponse: (res: { data: QCConfig }) => res.data,
      invalidatesTags: ['GustavoQCConfig'],
    }),
    getGustavoQCStats: build.query<QCStats, void>({
      query: () => '/ai/monitor/qc/stats',
      transformResponse: (res: { data: QCStats }) => res.data,
    }),
    listGustavoQCFailures: build.query<QCFailuresList, { page?: number; limit?: number }>({
      query: (params) => ({ url: '/ai/monitor/qc/failures', params }),
      transformResponse: (res: { data: QCFailuresList }) => res.data,
    }),

    // ─── Replay de conversación ────────────────────────────────────
    replayGustavoConversation: build.mutation<ReplayResult, { conversationId: string; messageIndex?: number; triggerText?: string; triggerTs?: number }>({
      query: ({ conversationId, messageIndex, triggerText, triggerTs }) => ({
        url:    `/ai/monitor/conversations/${conversationId}/replay`,
        method: 'POST',
        body: {
          ...(typeof messageIndex === 'number' ? { messageIndex } : {}),
          ...(triggerText ? { triggerText } : {}),
          ...(typeof triggerTs === 'number' ? { triggerTs } : {}),
        },
      }),
      transformResponse: (res: { data: ReplayResult }) => res.data,
    }),

    // ─── Hallucinations (Capa 1) ───────────────────────────────────
    listGustavoHallucinations: build.query<HallucinationsList, HallucinationsListParams>({
      query: (params) => ({ url: '/ai/monitor/hallucinations', params }),
      transformResponse: (res: { data: HallucinationsList }) => res.data,
      providesTags: ['GustavoHallucination'],
    }),
    getGustavoHallucinationStats: build.query<HallucinationStats, void>({
      query: () => '/ai/monitor/hallucinations/stats',
      transformResponse: (res: { data: HallucinationStats }) => res.data,
      providesTags: ['GustavoHallucination'],
    }),
    resolveGustavoHallucination: build.mutation<{ _id: string; status: string }, { id: string; generatedRuleId?: string }>({
      query: ({ id, generatedRuleId }) => ({
        url:    `/ai/monitor/hallucinations/${id}/resolve`,
        method: 'POST',
        body:   generatedRuleId ? { generatedRuleId } : {},
      }),
      transformResponse: (res: { data: { _id: string; status: string } }) => res.data,
      invalidatesTags: ['GustavoHallucination'],
    }),
    falsePositiveGustavoHallucination: build.mutation<{ _id: string; status: string }, string>({
      query: (id) => ({ url: `/ai/monitor/hallucinations/${id}/false-positive`, method: 'POST' }),
      transformResponse: (res: { data: { _id: string; status: string } }) => res.data,
      invalidatesTags: ['GustavoHallucination'],
    }),
    promoteHallucinationToBug: build.mutation<{ bugReportId: string; hallucinationId: string; alreadyPromoted?: boolean }, string>({
      query: (id) => ({ url: `/ai/monitor/hallucinations/${id}/to-bug`, method: 'POST' }),
      transformResponse: (res: { data: { bugReportId: string; hallucinationId: string; alreadyPromoted?: boolean } }) => res.data,
      invalidatesTags: ['GustavoHallucination', 'BugReport'],
    }),
    /** Preview del re-contacto: qué pregunta se reprocesaría (editable en el panel). */
    recontactPreview: build.query<{ prompt: string; channel: string; agentName: string | null }, string>({
      query: (id) => ({ url: `/ai/monitor/hallucinations/${id}/recontact-preview` }),
      transformResponse: (res: { data: { prompt: string; channel: string; agentName: string | null } }) => res.data,
    }),
    /** Re-contacto: Gustavo le ofrece al agente reprocesar la consulta que falló. */
    recontactHallucination: build.mutation<{ recontactId: string; channel: string; delivery: string }, { id: string; prompt?: string }>({
      query: ({ id, prompt }) => ({ url: `/ai/monitor/hallucinations/${id}/recontact`, method: 'POST', body: prompt ? { prompt } : {} }),
      transformResponse: (res: { data: { recontactId: string; channel: string; delivery: string } }) => res.data,
      invalidatesTags: ['GustavoHallucination'],
    }),
    flagConversationBug: build.mutation<{ bugReportId: string }, { conversationId: string; messageIndex: number; reason: string; expected?: string }>({
      query: ({ conversationId, messageIndex, reason, expected }) => ({
        url:    `/ai/monitor/conversations/${conversationId}/flag-bug`,
        method: 'POST',
        body:   { messageIndex, reason, expected },
      }),
      transformResponse: (res: { data: { bugReportId: string } }) => res.data,
      invalidatesTags: ['BugReport'],
    }),

    // ─── Audit log ──────────────────────────────────────────────────
    listGustavoAuditActions: build.query<AuditActionsList, AuditActionsListParams>({
      query: (params) => ({ url: '/ai/monitor/audit/actions', params }),
      transformResponse: (res: { data: AuditActionsList }) => res.data,
    }),
    getGustavoAuditSummary: build.query<AuditDailySummary, void>({
      query: () => '/ai/monitor/audit/summary',
      transformResponse: (res: { data: AuditDailySummary }) => res.data,
    }),

    // ─── Golden test set ──────────────────────────────────────────
    listGustavoGoldenCases: build.query<{ cases: GoldenCase[]; stats: GoldenStats }, void>({
      query: () => '/ai/monitor/golden',
      transformResponse: (res: { data: { cases: GoldenCase[]; stats: GoldenStats } }) => res.data,
      providesTags: ['GustavoGolden'],
    }),
    createGustavoGoldenCase: build.mutation<{ _id: string; name: string }, GoldenCaseInput>({
      query: (body) => ({ url: '/ai/monitor/golden', method: 'POST', body }),
      transformResponse: (res: { data: { _id: string; name: string } }) => res.data,
      invalidatesTags: ['GustavoGolden'],
    }),
    updateGustavoGoldenCase: build.mutation<{ _id: string; name: string }, { id: string; body: Partial<GoldenCaseInput> }>({
      query: ({ id, body }) => ({ url: `/ai/monitor/golden/${id}`, method: 'PATCH', body }),
      transformResponse: (res: { data: { _id: string; name: string } }) => res.data,
      invalidatesTags: ['GustavoGolden'],
    }),
    deleteGustavoGoldenCase: build.mutation<{ _id: string; deleted: boolean }, string>({
      query: (id) => ({ url: `/ai/monitor/golden/${id}`, method: 'DELETE' }),
      transformResponse: (res: { data: { _id: string; deleted: boolean } }) => res.data,
      invalidatesTags: ['GustavoGolden'],
    }),
    runGustavoGoldenCase: build.mutation<GoldenRunOutcome, string>({
      query: (id) => ({ url: `/ai/monitor/golden/${id}/run`, method: 'POST' }),
      transformResponse: (res: { data: GoldenRunOutcome }) => res.data,
      invalidatesTags: ['GustavoGolden'],
    }),
    runGustavoGoldenSuite: build.mutation<GoldenSuiteResult, { grupo?: string } | void>({
      // Sin `grupo` corre la suite entera; con `grupo`, esa sección sola.
      query: (arg) => ({ url: '/ai/monitor/golden/run-all', method: 'POST', body: { grupo: arg?.grupo } }),
      transformResponse: (res: { data: GoldenSuiteResult }) => res.data,
      invalidatesTags: ['GustavoGolden'],
    }),
    // Golden REALES: turnos de verdad con el usuario QA, corridos dentro del backend.
    listGoldenReal: build.query<{ casos: GoldenRealCaso[]; corridas: GoldenRealCorrida[] }, void>({
      query: () => '/ai/monitor/golden-real',
      transformResponse: (res: { data: { casos: GoldenRealCaso[]; corridas: GoldenRealCorrida[] } }) => res.data,
      providesTags: ['GustavoGolden'],
    }),
    runGoldenReal: build.mutation<{ id: string; yaCorriendo?: boolean }, { ids?: string[] } | void>({
      query: (arg) => ({ url: '/ai/monitor/golden-real/run', method: 'POST', body: { ids: arg?.ids } }),
      transformResponse: (res: { data: { id: string; yaCorriendo?: boolean } }) => res.data,
      invalidatesTags: ['GustavoGolden'],
    }),
    listGustavoGoldenRuns: build.query<{ runs: GoldenRunHistoryItem[] }, { id: string; limit?: number }>({
      query: ({ id, limit }) => ({ url: `/ai/monitor/golden/${id}/runs`, params: limit ? { limit } : undefined }),
      transformResponse: (res: { data: { runs: GoldenRunHistoryItem[] } }) => res.data,
    }),
  }),
});

export interface GoldenRealCaso { id: string; grupo: string; nombre: string; turnos: number; origen: string | null }
export interface GoldenRealTurno { mensaje: string; respuesta: string; tools: string[]; simuladas: string[]; fallas: string[]; ms: number }
export interface GoldenRealCorrida {
  _id: string;
  estado: 'corriendo' | 'terminada' | 'error';
  inicio: string;
  fin?: string;
  commit?: string | null;
  ok?: number;
  total?: number;
  error?: string;
  casos?: Array<{ id: string; nombre: string; ok: boolean; turnos: GoldenRealTurno[]; limpieza: string[] }>;
}

export type GoldenAssertionKind =
  | 'must_call_tool'
  | 'must_not_call_tool'
  | 'must_contain'
  | 'must_not_contain'
  | 'must_have_no_hallucinations'
  | 'must_call_tool_with'
  | 'must_cite_code'
  | 'must_not_cite_code';

export interface GoldenAssertion {
  kind:  GoldenAssertionKind;
  value: string;
  /** Solo must_call_tool_with: subset de argumentos esperado (clave plana o dot-path). */
  expectedArgs?: Record<string, unknown>;
}

/** Mensaje de la conversación semilla de un caso multiturno (solo lectura en la UI). */
export interface GoldenSeedMessage {
  role:      'user' | 'assistant' | 'tool';
  content:   string;
  toolName?: string;
}

export interface GoldenCase {
  _id:               string;
  name:              string;
  description:       string | null;
  surface:           GustavoSurface;
  /** Sección de la suite (`seccionesGolden.ts` del backend). Sirve para correr una parte. */
  grupo:             string;
  userPrompt:        string;
  assertions:        GoldenAssertion[];
  seedMessages?:     GoldenSeedMessage[];
  active:            boolean;
  impersonateUserId: string | null;
  lastRunAt:         string | null;
  lastPassed:        boolean | null;
  lastRunFailures:   number | null;
  createdAt:         string;
  updatedAt:         string;
}

export interface GoldenCaseInput {
  name:              string;
  description?:      string;
  surface?:          GustavoSurface;
  userPrompt:        string;
  assertions:        GoldenAssertion[];
  active?:           boolean;
  impersonateUserId?: string | null;
}

export interface GoldenStats {
  total:    number;
  active:   number;
  ranEver:  number;
  passing:  number;
  failing:  number;
}

export interface GoldenRunFailure {
  kind:   GoldenAssertionKind;
  value:  string;
  reason: string;
}

export interface GoldenRunOutcome {
  testCaseId:          string;
  testName:            string;
  passed:              boolean;
  failures:            GoldenRunFailure[];
  durationMs:          number;
  replayedText:        string;
  replayedToolCalls:   string[];
  activePromptVersion: number | null;
  rulesApplied:        number;
}

export interface GoldenSuiteResult {
  ranAt:   string;
  total:   number;
  passed:  number;
  failed:  number;
  /** La sección que se corrió, o null si fue la suite entera. */
  grupo?:  string | null;
  results: GoldenRunOutcome[];
}

export interface GoldenRunHistoryItem {
  _id:                 string;
  runAt:               string;
  passed:              boolean;
  failures:            GoldenRunFailure[];
  durationMs:          number;
  replayedText:        string;
  replayedToolCalls:   string[];
  activePromptVersion: number | null;
  rulesApplied:        number;
}

export type AuditActionCategory = 'crear' | 'actualizar' | 'comunicar' | 'eliminar' | 'otro';

export interface AuditActionItem {
  _id:               string;
  toolName:          string;
  action:            string;
  category:          AuditActionCategory;
  status:            'ok' | 'error';
  errorMessage:      string | null;
  durationMs:        number;
  writeAction:       boolean;
  resultModel:       string | null;
  resultId:          string | null;
  resultLabel:       string | null;
  undoneAt:          string | null;
  conversationId:    string | null;
  conversationTitle: string | null;
  createdAt:         string;
  user: {
    _id:    string;
    name:   string;
    email:  string | null;
    role:   string | null;
    avatar: string | null;
  } | null;
}

export interface AuditActionsList {
  page:       number;
  limit:      number;
  total:      number;
  totalPages: number;
  items:      AuditActionItem[];
}

export interface AuditActionsListParams {
  page?:       number;
  limit?:      number;
  from?:       string;
  to?:         string;
  userId?:     string;
  filter?:     'write' | 'error' | 'all';
  toolName?:   string;
  undoneOnly?: boolean;
}

export interface AuditDailySummary {
  totalActions: number;
  errors:       number;
  undone:       number;
  byCategory:   Record<AuditActionCategory, number>;
}

export type HallucinationKind = 'dato_sin_fuente' | 'citacion_incorrecta' | 'link_inventado' | 'accion_fantasma';
export type HallucinationSeverity = 'low' | 'medium' | 'high' | 'critical';
export type HallucinationStatus = 'new' | 'resolved' | 'false_positive' | 'promoted';
export type GustavoSurface = 'agent' | 'public';

export interface HallucinationItem {
  _id:               string;
  kind:              HallucinationKind;
  severity:          HallucinationSeverity;
  detail:            string;
  evidence: {
    claim:           string;
    expected?:       string;
    toolsCalled:     string[];
  };
  status:            HallucinationStatus;
  channel:           'web' | 'whatsapp';
  surface:           GustavoSurface;
  sessionId:         string | null;
  conversationId:    string | null;
  conversationTitle: string;
  messageIndex:      number;
  assistantSnippet:  string | null;
  userPromptSnippet: string | null;
  detectedAt:        string;
  notifiedAt:        string | null;
  notificationChannels: Array<'browser' | 'whatsapp'>;
  resolvedAt:        string | null;
  generatedRuleId:   string | null;
  bugReportId?:      string | null;
  user: {
    _id:    string;
    name:   string;
    email:  string | null;
    role:   string | null;
    avatar: string | null;
  } | null;
}

export interface HallucinationsList {
  page:       number;
  limit:      number;
  total:      number;
  totalPages: number;
  items:      HallucinationItem[];
}

export interface HallucinationsListParams {
  page?:     number;
  limit?:    number;
  kind?:     HallucinationKind | 'all';
  severity?: HallucinationSeverity | 'all';
  status?:   HallucinationStatus | 'all';
  surface?:  GustavoSurface | 'all';
  userId?:   string;
}

export interface HallucinationStats {
  today: {
    total:  number;
    byKind: Record<HallucinationKind, number>;
  };
  sparkline: Array<{ date: string; count: number }>;
  byKind7d:  Record<HallucinationKind, number>;
  status:    Record<HallucinationStatus, number>;
}

export interface ReplayToolCall {
  id?:  string;
  name: string;
  args: unknown;
}

export interface ReplayResult {
  conversationId: string;
  messageIndex:   number;
  triggerUser:    { name: string; role: string };
  triggerPrompt:  string;
  original: {
    text:      string;
    toolCalls: ReplayToolCall[];
    createdAt: string;
  };
  replayed: {
    text:                 string;
    toolCalls:            ReplayToolCall[];
    finishReason:         string;
    durationMs:           number;
    model:                string;
    activePromptVersion:  number | null;
    rulesApplied:         number;
  };
  diff: {
    textChanged:      boolean;
    toolsChanged:     boolean;
    addedToolNames:   string[];
    removedToolNames: string[];
  };
}

export interface QCConfig {
  enabled:               boolean;
  samplePct:             number;
  alwaysCheckCategories: string[];
}

export interface QCStats {
  today:     { ok: number; retry: number; error: number; skip: number; total: number };
  sparkline: Array<{ date: string; ok: number; retry: number; total: number }>;
}

export interface QCFailureItem {
  _id:               string;
  conversationId:    string;
  conversationTitle: string;
  messageIndex:      number;
  verdict:           'ok' | 'retry' | 'error' | 'skip';
  reason:            string | null;
  score:             number | null;
  samplePct:         number | null;
  evaluatedAt:       string;
  evalDurationMs:    number | null;
  user: {
    _id: string; name: string; email: string | null;
    role: string | null; avatar: string | null;
  } | null;
  assistantSnippet:  string | null;
  userPromptSnippet: string | null;
}

export interface QCFailuresList {
  page:       number;
  limit:      number;
  total:      number;
  totalPages: number;
  items:      QCFailureItem[];
}

export interface GustavoPromptVersion {
  _id:       string;
  version:   number;
  template:  string;
  active:    boolean;
  label:     string | null;
  notes:     string | null;
  createdBy: { _id: string; name: string; email: string | null } | null;
  createdAt: string;
  updatedAt: string;
}

export interface ActivePromptResponse {
  active:          GustavoPromptVersion | null;
  fallback:        string;
  isUsingFallback: boolean;
}

export type QualityFlagKind =
  | 'tool_loop' | 'empty_args' | 'hesitant_phrase' | 'high_failure_rate'
  | 'too_short' | 'too_long' | 'repeated_prompt' | 'no_assistant_reply';
export type QualitySeverity = 'info' | 'warning' | 'critical';

export interface QualityFlag {
  kind:          QualityFlagKind;
  severity:      QualitySeverity;
  detail:        string;
  messageIndex?: number;
  toolName?:     string;
}

export interface ConversationQuality {
  conversationId:  string;
  score:           number;
  flags:           QualityFlag[];
  messageCount:    number;
  toolCallsCount:  number;
  toolErrorsCount: number;
  hasFlags:        boolean;
}

export interface QualityScanItem extends ConversationQuality {
  title:         string;
  lastMessageAt: string;
  user: {
    _id: string; name: string; email: string | null;
    role: string | null; avatar: string | null;
  } | null;
}

export interface QualityScanParams {
  page?:        number;
  limit?:       number;
  range?:       '24h' | '7d' | '30d';
  flag?:        QualityFlagKind | 'all';
  minSeverity?: QualitySeverity;
}

export interface QualityScanResult {
  page:         number;
  limit:        number;
  total:        number;
  totalPages:   number;
  totalScanned: number;
  avgScore:     number;
  flagCounts:   Record<QualityFlagKind, number>;
  items:        QualityScanItem[];
}

export type RuleCategory = 'tone' | 'safety' | 'data' | 'workflow' | 'escalation' | 'format' | 'general';

/** A qué Gustavo aplica la regla: agente (CRM), cliente (web) o ambos. */
export type RuleAudience = 'agent' | 'client' | 'both';

export interface GustavoRule {
  _id:           string;
  inputText:     string;
  rule:          string;
  category:      RuleCategory;
  audience:      RuleAudience;
  enabled:       boolean;
  source:        'manual' | 'from_feedback' | 'from_conversation';
  appliedCount:  number;
  conversationId: string | null;
  messageIndex:  number | null;
  createdAt:     string;
  updatedAt:     string;
}

export interface GeneratedRule {
  rule:      string;
  category:  RuleCategory;
  audience:  RuleAudience;
  rationale: string;
}

export interface CreateGustavoRuleBody {
  inputText:       string;
  rule:            string;
  category:        RuleCategory;
  audience?:       RuleAudience;
  source?:         'manual' | 'from_feedback' | 'from_conversation';
  conversationId?: string;
  messageIndex?:   number;
}

/** Estado del control manual de una charla. */
export interface EstadoManual {
  activo:     boolean;
  desde?:     string | null;
  porNombre?: string | null;
}

export interface GustavoToolEntry {
  toolName:      string;
  description:   string;
  /** Módulo al que pertenece: agrupa las tools en lóbulos en el mapa neuronal. */
  module:        string;
  action:        string | null;
  enabled:       boolean;
  flagReason:    string | null;
  flagUpdatedAt: string | null;
  calls24h:      number;
  errors24h:     number;
  avgMs24h:      number;
  lastCall:      string | null;
}

// ─── Tipos del monitor ──────────────────────────────────────────────────────

export interface MonitorListParams {
  page?:     number;
  limit?:    number;
  userId?:   string;
  toolName?: string;
  search?:   string;
  from?:     string;
  to?:       string;
  status?:   'all' | 'errors';
}

export interface MonitorConversationItem {
  _id:             string;
  title:           string;
  lastMessageAt:   string;
  createdAt:       string;
  messageCount:    number;
  toolCallsCount:  number;
  toolErrorsCount: number;
  totalDurationMs: number;
  user: {
    _id: string; name: string; email: string | null;
    role: string | null; avatar: string | null;
  } | null;
}

export interface MonitorConversationsList {
  page:       number;
  limit:      number;
  total:      number;
  totalPages: number;
  items:      MonitorConversationItem[];
}

export interface MonitorMessage {
  role:        'user' | 'assistant' | 'tool' | 'system';
  content:     string;
  toolCalls?:  Array<{ id: string; name: string; args: Record<string, unknown> }>;
  toolCallId?: string;
  toolName?:   string;
  attachments?: Array<{ type: string; url: string; name?: string }>;
  /** Entró como nota de voz por WhatsApp: `content` es la transcripción. */
  voiceNote?:  boolean;
  /** La nota de voz en sí, para escucharla en vez de leer solo la transcripción. */
  audioInUrl?: string;
  /** En los mensajes de Gustavo: la nota de voz con la que contestó. */
  audioOutUrl?: string;
  createdAt:   string;
}

export interface MonitorToolLog {
  _id:          string;
  toolName:     string;
  args:         Record<string, unknown>;
  status:       'ok' | 'error';
  errorMessage: string | null;
  durationMs:   number;
  resultCount:  number | null;
  resultModel:  string | null;
  resultId:     string | null;
  createdAt:    string;
}

export interface MonitorConversationDetail {
  _id:            string;
  title:          string;
  lastMessageAt:  string;
  createdAt:      string;
  contextSummary: string | null;
  user:           MonitorConversationItem['user'];
  messages:       MonitorMessage[];
  toolLogs:       MonitorToolLog[];
}

export interface MonitorToolStat {
  toolName:  string;
  calls:     number;
  errors:    number;
  errorRate: number;
  avgMs:     number;
  maxMs:     number;
  lastCall:  string;
}

export interface MonitorToolStats {
  range: { from: string; to: string };
  tools: MonitorToolStat[];
  total: { calls: number; errors: number };
}

export interface MonitorToolExecutionItem extends MonitorToolLog {
  conversationId: string | null;
  user: { _id: string; name: string; email: string | null; role: string | null } | null;
}

export interface MonitorToolExecutions {
  toolName: string;
  total:    number;
  items:    MonitorToolExecutionItem[];
}

interface FeedbackBucket {
  up: number; down: number; total: number;
  satisfactionPct: number | null;
}
export interface FeedbackStats {
  today:     FeedbackBucket;
  yesterday: FeedbackBucket;
  last7d:    FeedbackBucket;
  sparkline: Array<{ date: string; up: number; down: number }>;
}

export interface NegativeFeedbackItem {
  _id:               string;
  conversationId:    string;
  conversationTitle: string;
  messageIndex:      number;
  comment:           string | null;
  createdAt:         string;
  user: {
    _id: string; name: string; email: string | null;
    role: string | null; avatar: string | null;
  } | null;
  assistantSnippet:  string | null;
  userPromptSnippet: string | null;
}

export interface NegativeFeedbacksList {
  page:       number;
  limit:      number;
  total:      number;
  totalPages: number;
  items:      NegativeFeedbackItem[];
}

export const {
  useListConversationsQuery,
  useGetConversationQuery,
  useDeleteConversationMutation,
  useListGustavoAuditQuery,
  useGetGustavoAuditByIdQuery,
  useUndoGustavoActionMutation,
  useGetGustavoStatsQuery,
  useGetGustavoConsumptionQuery,
  useGetGustavoGreetingQuery,
  useGetMainConversationQuery,
  useGetGustavoNoticesQuery,
  useMarkGustavoNoticesSeenMutation,
  useListMonitorConversationsQuery,
  useGetMonitorConversationQuery,
  useGetMonitorToolStatsQuery,
  useGetMonitorToolExecutionsQuery,
  useLazyGetMonitorConversationQuery,
  useLazyGetMonitorToolExecutionsQuery,
  useGetFeedbackStatsQuery,
  useListNegativeFeedbacksQuery,
  useResolveFeedbackMutation,
  useSubmitGustavoFeedbackMutation,
  useListAllGustavoToolsQuery,
  useSetGustavoToolFlagMutation,
  useListGustavoRulesQuery,
  useGenerateGustavoRuleMutation,
  useGenerateGustavoRuleFromConversationMutation,
  useCreateGustavoRuleMutation,
  useUpdateGustavoRuleMutation,
  useDeleteGustavoRuleMutation,
  useScanGustavoQualityQuery,
  useGetGustavoQualityAnalysisQuery,
  useGetActiveGustavoPromptQuery,
  useListGustavoPromptVersionsQuery,
  useCreateGustavoPromptVersionMutation,
  useActivateGustavoPromptVersionMutation,
  useDeleteGustavoPromptVersionMutation,
  useGetGustavoQCConfigQuery,
  useSetGustavoQCConfigMutation,
  useGetGustavoQCStatsQuery,
  useListGustavoQCFailuresQuery,
  useReplayGustavoConversationMutation,
  useListGustavoHallucinationsQuery,
  useGetGustavoHallucinationStatsQuery,
  useResolveGustavoHallucinationMutation,
  useFalsePositiveGustavoHallucinationMutation,
  usePromoteHallucinationToBugMutation,
  useRecontactHallucinationMutation,
  useLazyRecontactPreviewQuery,
  useFlagConversationBugMutation,
  useListGustavoAuditActionsQuery,
  useGetGustavoAuditSummaryQuery,
  useListGustavoGoldenCasesQuery,
  useCreateGustavoGoldenCaseMutation,
  useUpdateGustavoGoldenCaseMutation,
  useDeleteGustavoGoldenCaseMutation,
  useRunGustavoGoldenCaseMutation,
  useRunGustavoGoldenSuiteMutation,
  useListGoldenRealQuery,
  useRunGoldenRealMutation,
  useListGustavoGoldenRunsQuery,
  useListGustavoFlowsQuery,
  useGetGustavoV7PilotQuery,
  useGetGustavoChangelogQuery,
  useListGustavoChatsQuery,
  useListGustavoPresenciaQuery,
  useGetFichasAgentesQuery,
  useListVozTenantsQuery,
  useListVozAgentesQuery,
  useAbrirSesionVozMutation,
  useListVozToolNamesQuery,
  useSetGustavoManualMutation,
  useSendGustavoManualMutation,
  useGetIncidentsSummaryQuery,
  useListIncidentsQuery,
  useUpdateIncidentMutation,
  useGenerateIncidentGoldenDraftMutation,
  useRunIncidentDetectionMutation,
  useListQualityFindingsQuery,
  useGetQualityMetricasQuery,
  useLabelQualityFindingMutation,
  useRunQualityMutation,
  useGetContinuousQaQuery,
  useConfigureContinuousQaMutation,
  useQueueContinuousQaNowMutation,
  usePasarQualityACodexMutation,
  useLazyGetQualityContextoQuery,
  usePushQualityArregloMutation,
  useArmarQualityMensajeMutation,
  useEnviarQualityMensajeMutation,
  usePromoteIncidentToBugMutation,
} = gustavoApi;
